// Note: There may be cases where there are temporarily multiple headers on the page simultaneously.
//
// This is specifically the case on the Zoopla mobile codebase (jQuery Mobile). This does AJAX-page transitions
// but the existing page is not removed from the DOM until after the page we're transitioning to has been injected.
// This means the code below  may execute in an environment where there are multiple headers. In that vain, we code
// defensively to apply any DOM updates to all headers on the page.
type ResponseType = {
    data: {
        user: {
            loggedIn: string;
        };
    };
};

declare global {
    interface WindowEventMap {
        authentication: CustomEvent;
    }
}

export default function authentication() {
    return function () {
        const loggedInAttributeName = 'data-is-logged-in';
        const sessionStorageKey = 'z-bui-component-header-is-logged-in';

        function getHeaders() {
            return [].slice.call(
                document.querySelectorAll('[data-testid="header"]')
            );
        }

        const loggedInCache = {
            set: function (isLoggedIn: boolean) {
                window.sessionStorage.setItem(
                    sessionStorageKey,
                    isLoggedIn.toString()
                );
            },
            get: function () {
                return (
                    window.sessionStorage.getItem(sessionStorageKey) === 'true'
                );
            }
        };

        function setHeaderLoggedInState(isLoggedIn: boolean) {
            getHeaders().forEach(function (header: HTMLElement) {
                isLoggedIn
                    ? header.setAttribute(loggedInAttributeName, 'true')
                    : header.removeAttribute(loggedInAttributeName);
            });
        }

        function getGraphQLAPI() {
            const host = window.location.hostname;
            if (host.endsWith('zoopla.dev')) {
                if (host.startsWith('www.zoopla.dev')) {
                    return {
                        apiUrl: 'https://gql-proxy.zoopla.dev/graphql',
                        apiKey: '3Vzj2wUfaP3euLsV4NV9h3UAVUR3BoWd5clv9Dvu'
                    };
                }
                return {
                    apiUrl: 'https://gql-proxy.integ.zoopla.dev/graphql',
                    apiKey: 'q71XbbbNQyeK8t7STsdS31XogDTTvcT1OrnUGYM6'
                };
            }

            if (
                host.indexOf('qa.zoopla') >= 0 ||
                host.indexOf('local.zoopla') >= 0 ||
                host.indexOf('dev.zoopla') >= 0 ||
                host.indexOf('develop.zoopla') >= 0 ||
                host.indexOf('localhost') >= 0
            )
                return {
                    apiUrl: 'https://api-graphql-lambda.integ.zoopla.co.uk/graphql',
                    apiKey: 'q71XbbbNQyeK8t7STsdS31XogDTTvcT1OrnUGYM6'
                };
            return {
                apiUrl: 'https://api-graphql-lambda.prod.zoopla.co.uk/graphql',
                apiKey: '3Vzj2wUfaP3euLsV4NV9h3UAVUR3BoWd5clv9Dvu'
            };
        }

        function init() {
            const { apiUrl, apiKey } = getGraphQLAPI();
            const query =
                '{"operationName":"q","variables":{},"query":"query q{\\nuser{\\nloggedIn\\n}\\n}\\n"}';
            const headers = getHeaders();

            if (!headers.length) {
                return;
            }

            function onFetchedLoggedInState(response: ResponseType) {
                const isLoggedIn =
                    !!response.data &&
                    !!response.data.user &&
                    !!response.data.user.loggedIn;
                loggedInCache.set(isLoggedIn);
                setHeaderLoggedInState(isLoggedIn);
            }

            function fetchLoggedInState(
                callback: typeof onFetchedLoggedInState
            ) {
                const req = new window.XMLHttpRequest();
                req.open('POST', apiUrl, true);
                req.setRequestHeader('X-Api-Key', apiKey);
                req.setRequestHeader('Content-Type', 'application/json');
                req.withCredentials = true;
                req.onreadystatechange = function () {
                    if (req.readyState === XMLHttpRequest.DONE) {
                        try {
                            if (req.status !== 200) {
                                throw Error(
                                    'Error occurred when fetching universal header. Status code: ' +
                                        req.status +
                                        '.'
                                );
                            }
                            const response = JSON.parse(req.response);
                            if (!response.data) {
                                throw Error(
                                    'Error: data format is not correct.'
                                );
                            }
                            callback(response);
                        } catch (error) {
                            console.error((error as Error).message);
                        }
                    }
                };
                req.send(query);
            }

            function setSignInURL() {
                const isSigninPage = window.location.pathname === '/signin/';
                const currentUrl = !isSigninPage
                    ? window.location.pathname + window.location.search
                    : window.location.pathname;

                getHeaders().forEach(function (header: HTMLElement) {
                    const signInAnchors = [].slice.call(
                        header.querySelectorAll('[data-header-signin]')
                    );

                    signInAnchors.forEach(function (anchor: HTMLElement) {
                        const urlTemplate: string | undefined =
                            anchor.dataset.headerSignin;
                        if (!urlTemplate) {
                            return;
                        }

                        if (
                            isSigninPage &&
                            (anchor.getAttribute('data-testid') ===
                                'header-profile-sign-in' ||
                                anchor.getAttribute('data-testid') ===
                                    'header-mobile-sign-in')
                        ) {
                            anchor.setAttribute('href', '/signin/');
                        } else {
                            anchor.setAttribute(
                                'href',
                                urlTemplate.replace(
                                    /{URL}/g,
                                    encodeURIComponent(currentUrl)
                                )
                            );
                        }
                    });
                });
            }

            setSignInURL();
            fetchLoggedInState(onFetchedLoggedInState);

            window.addEventListener('authentication', (e: CustomEvent) => {
                if (!e.detail) {
                    return;
                }

                // both login and logout status need to fetch and update browser cache again
                if (typeof e.detail.authenticated === 'boolean') {
                    fetchLoggedInState(onFetchedLoggedInState);
                }
            });
        }

        // Immediately execute setting of logged-in/out state
        setHeaderLoggedInState(loggedInCache.get());

        // Defer the rest if we can until DOMContentLoaded
        if (/complete|interactive|loaded/.test(document.readyState)) {
            init();
        } else {
            document.addEventListener('DOMContentLoaded', init);
        }
    };
}
